import { compose } from 'recompose'

import { EDITOR_CONTENT } from 'components/Layout/NoteView'
import connectToDatabase from '@slite/editor/lib/connects/connectToDatabase'
import provideDatabaseValue from '@slite/editor/lib/connects/provideDatabaseValue'

// Shared plugins
import { plugins as BaseEditorPlugins } from './EditorPlugins'
import makeCommentDisplayPlugin from './CommentDisplayPlugin'
import makeLinkPlugin from './link'
import makeBlockLinkPlugin from './block-link'
import makeMentionPlugin from './mention'
import makeNoteLinkPlugin from './NoteLinkInAppPlugin'
import makeIntegrationSuggestionPlugin from './integration-suggestion'
import makeOutlineMenuPlugin from './OutlineMenuPlugin'
import makePresencePlugin from './PresencePlugin/index'
import makeShareToPlugin from './share-to'

import CommentsEditor from '../CommentEditor'
import FileUploadPlugin from './file-upload'
import UnfurlPlugin from './unfurl'
import makeEmbedPlaceholderPluginDecorator from './EmbedPlaceholderPlugin/Decorator'
import LaunchPlugin from './LaunchPlugin'
import { measureTimingEnd } from 'data/metrics'

const EDITOR_BOTTOM_SPACE = 300

export default async ({
  getFullNotePath,
  focusPrevious,
  getUser,
  addMediaFileToNote,
  fetchUsers,
  fetchNotes,
  getCurrentUser,
  createThread,
  iframelyApiKey,
  unfurlHandler,
  getApplications,
  integrationSuggestionCallbacks,
  createInfoToasterMessage,
  track,
  hasEditorNextFeatureFlag = false,
  appDomain,
  organizationDomain,
  showNoteByToken,
  setActiveModal,
  getNoteId,
  hasDatabaseFeatureFlag = false,
}) => {
  const SliteEditorPlugins = hasEditorNextFeatureFlag
    ? (await import(/* webpackChunkName: "editor-next" */ './EditorNextPlugins'))
        .plugins
    : BaseEditorPlugins

  const provideRemoteDatabaseSync = compose(
    connectToDatabase(),
    provideDatabaseValue()
  )
  return Promise.all([
    SliteEditorPlugins.CollaborationPlugin(),
    SliteEditorPlugins.CrashPlugin(),
    SliteEditorPlugins.CorePlugin(),
    SliteEditorPlugins.FullWidthPlugin({ containerId: EDITOR_CONTENT }),
    BaseEditorPlugins.SyncPlugin(),
    SliteEditorPlugins.AnalyticsPlugin({ track }),
    SliteEditorPlugins.ToolbarPlugin({
      containerId: EDITOR_CONTENT,
      contentInsets: { top: 60 },
    }),
    SliteEditorPlugins.GutterBlockPlugin(),
    SliteEditorPlugins.DndPlugin(),
    FileUploadPlugin(),
    makeBlockLinkPlugin({
      BlockLinkPlugin: SliteEditorPlugins.BlockLinkPlugin,
    })({ getFullNotePath, getNoteId }), // Must be before the CONTINUOUS_BLOCK plugins
    SliteEditorPlugins.PlaceholderPlugin(),
    SliteEditorPlugins.MentionPickerPlugin({
      fetchUsers,
      fetchNotes,
      getCurrentUser,
      bottomSpace: EDITOR_BOTTOM_SPACE,
    }),
    SliteEditorPlugins.CommandPickerPlugin({
      bottomSpace: EDITOR_BOTTOM_SPACE,
    }),
    SliteEditorPlugins.EmojiPickerPlugin({ bottomSpace: EDITOR_BOTTOM_SPACE }),
    SliteEditorPlugins.TodoPlugin(),
    SliteEditorPlugins.LineBreakPlugin(),
    SliteEditorPlugins.TablePlugin(),
    SliteEditorPlugins.FirstLinePlugin({ focusPrevious }),
    makeOutlineMenuPlugin({ utils: SliteEditorPlugins.utils })(),
    SliteEditorPlugins.BlockquotePlugin(),
    SliteEditorPlugins.HintPlugin(),
    SliteEditorPlugins.CodePlugin(),
    SliteEditorPlugins.ImageGalleryPlugin(),
    SliteEditorPlugins.ImagePlugin({ enableLightbox: true }),
    SliteEditorPlugins.PdfPlugin(),
    makeMentionPlugin({ MentionPlugin: SliteEditorPlugins.MentionPlugin })({
      bottomSpace: EDITOR_BOTTOM_SPACE,
    }),
    makeNoteLinkPlugin({ NoteLinkPlugin: SliteEditorPlugins.NoteLinkPlugin })(),
    SliteEditorPlugins.FilePlugin({ getUser, addMediaFileToNote }),
    makeLinkPlugin({ LinkPlugin: SliteEditorPlugins.LinkPlugin })({
      fetchNotes,
      isEditable: true,
      appDomain,
      organizationDomain,
      showNoteByToken,
    }),
    SliteEditorPlugins.SeparatorPlugin(),
    UnfurlPlugin({ handler: unfurlHandler }),
    SliteEditorPlugins.EmbedPlugin({ enableLightbox: true, iframelyApiKey }),
    SliteEditorPlugins.EmbedPlaceholderPlugin({
      getApplications,
      decorator: makeEmbedPlaceholderPluginDecorator(
        integrationSuggestionCallbacks
      ),
    }),
    SliteEditorPlugins.DatabasePlugin && hasDatabaseFeatureFlag
      ? SliteEditorPlugins.DatabasePlugin({
          provideDatabaseSync: provideRemoteDatabaseSync,
          getCurrentUser,
          getUser,
          fetchUsers,
          fetchNotes,
          makeNoteLinkPlugin,
          setActiveModal,
        })
      : {},
    makeCommentDisplayPlugin({
      CommentPlugin: SliteEditorPlugins.CommentPlugin,
    })({
      CommentsEditor,
      createThread,
    }),
    SliteEditorPlugins.RichTextPlugin(),
    SliteEditorPlugins.HighlightPlugin(),
    SliteEditorPlugins.TypingPlugin(),
    LaunchPlugin({
      setActiveModal,
    }),
    SliteEditorPlugins.LastLinePlugin({
      bottomSpace: EDITOR_BOTTOM_SPACE,
    }),
    SliteEditorPlugins.BlockActionPlugin(),
    SliteEditorPlugins.IntegrationTilePlugin(),
    makeIntegrationSuggestionPlugin({ utils: SliteEditorPlugins.utils })(
      integrationSuggestionCallbacks
    ),
    makeShareToPlugin({
      ClipboardPlugin: SliteEditorPlugins.ClipboardPlugin,
      utils: SliteEditorPlugins.utils,
    })({
      getApplications: () =>
        getApplications().filter(application =>
          application.features.includes('shareTo')
        ),
    }),
    SliteEditorPlugins.ClipboardPlugin({ getFullNotePath }),
    SliteEditorPlugins.LightboxPlugin({ getUser }),
    SliteEditorPlugins.ToastPlugin(createInfoToasterMessage),
    makePresencePlugin({ utils: SliteEditorPlugins.utils })(),
    SliteEditorPlugins.UnknownBlockPlugin(),
    {
      onConstruct(editor, next) {
        measureTimingEnd('TIME_TO_CREATE_NOTE')
        measureTimingEnd('TIME_TO_LOAD_FIRST_NOTE')
        next()
      },
    },
  ])
}
